import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  ArrowLeft, Headphones, MessageSquare, Mic, Send, Sparkles, ChevronRight,
} from 'lucide-react';
import logoImg from '../../assets/a50295a0af56ffb8dcd053f3d73ca9d37c948ba6.png';
import logoDarkImg from '../../assets/ff6cec488a700597f19b04a772a6a682dadfe9e6.png';
import { useDemoAIChat } from '../hooks/useDemoAIChat';
import { useDemoRealtime } from '../hooks/useDemoRealtime';
import { createDemoLead } from '../services/api';

type DemoMode = 'chat' | 'voice';

interface DemoScenario {
  id: string;
  title: string;
  difficulty: 'Facil' | 'Medio' | 'Dificil';
  difficultyColor: string;
  description: string;
  clientPersona: string;
  clientMood: string;
}

interface DemoMessage {
  id: string;
  speaker: 'operator' | 'client' | 'system';
  text: string;
}

interface DemoLeadAccess {
  id: string;
  name: string;
  email: string;
  company: string;
  sessionToken: string;
}

const DEMO_LIMIT = 10;
const DEMO_LEAD_STORAGE_KEY = 'simuladesk_demo_session';

const SCENARIOS: DemoScenario[] = [
  {
    id: 'atraso',
    title: 'Cliente irritado com atraso',
    difficulty: 'Dificil',
    difficultyColor: 'var(--error)',
    description: 'Cliente ligou pela terceira vez porque o pedido segue atrasado. O objetivo e demonstrar empatia e conduzir para uma solucao.',
    clientPersona: 'Carlos, 42 anos, empresario',
    clientMood: 'Muito irritado',
  },
  {
    id: 'cobranca',
    title: 'Cobranca indevida',
    difficulty: 'Medio',
    difficultyColor: 'var(--warning)',
    description: 'Cliente identificou cobranca duplicada na fatura e quer uma explicacao clara e resolucao sem atrito.',
    clientPersona: 'Maria, 35 anos, professora',
    clientMood: 'Preocupada',
  },
  {
    id: 'retencao',
    title: 'Pedido de cancelamento',
    difficulty: 'Facil',
    difficultyColor: 'var(--success)',
    description: 'Cliente quer cancelar por preco. A ideia e explorar a motivacao e tentar reter com abordagem consultiva.',
    clientPersona: 'Pedro, 28 anos, designer',
    clientMood: 'Decidido',
  },
];

function useThemeDetector() {
  const [isDark, setIsDark] = useState(() => document.documentElement.getAttribute('data-theme') === 'dark');
  useEffect(() => {
    const obs = new MutationObserver(() => setIsDark(document.documentElement.getAttribute('data-theme') === 'dark'));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);
  return isDark;
}

function createLocalId(prefix: string) {
  const value = globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : Math.random().toString(36).slice(2, 10);
  return `${prefix}-${value}`;
}

function DemoHeader() {
  const navigate = useNavigate();
  const isDark = useThemeDetector();

  return (
    <header style={{
      height: '64px',
      borderBottom: '1px solid var(--border)',
      backgroundColor: 'var(--card)',
      display: 'flex',
      alignItems: 'center',
      padding: '0 24px',
      gap: '16px',
    }}>
      <button
        onClick={() => navigate('/landing')}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          fontFamily: 'var(--font-family-inter)',
          fontSize: 'var(--text-sm)',
          fontWeight: 'var(--font-weight-medium)',
          color: 'var(--muted-foreground)',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
        }}
      >
        <ArrowLeft size={16} /> Voltar
      </button>
      <div style={{ width: '1px', height: '24px', backgroundColor: 'var(--border)' }} />
      <img src={isDark ? logoDarkImg : logoImg} alt="SimulaDesk" style={{ height: '28px', width: 'auto' }} />
      <span style={{
        fontFamily: 'var(--font-family-inter)',
        fontSize: 'var(--text-sm)',
        fontWeight: 'var(--font-weight-semibold)',
        color: 'var(--foreground)',
      }}>
        Demo Viva com IA
      </span>
    </header>
  );
}

export function InteractiveDemo() {
  const navigate = useNavigate();
  const [selectedScenario, setSelectedScenario] = useState<string>('atraso');
  const [mode, setMode] = useState<DemoMode>('chat');
  const [messages, setMessages] = useState<DemoMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const [operatorTurns, setOperatorTurns] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [isStartingVoice, setIsStartingVoice] = useState(false);
  const [leadAccess, setLeadAccess] = useState<DemoLeadAccess | null>(() => {
    if (typeof window === 'undefined') return null;
    const raw = window.sessionStorage.getItem(DEMO_LEAD_STORAGE_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as DemoLeadAccess;
    } catch {
      return null;
    }
  });
  const [leadForm, setLeadForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    role: '',
  });
  const [isSubmittingLead, setIsSubmittingLead] = useState(false);
  const [leadError, setLeadError] = useState<string | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const operatorTurnsRef = useRef(0);
  const isCompleteRef = useRef(false);

  const scenario = useMemo(
    () => SCENARIOS.find((item) => item.id === selectedScenario) || SCENARIOS[0],
    [selectedScenario],
  );

  const {
    messages: aiMessages,
    sendMessage,
    isTyping,
    aiError,
    cancelTyping,
  } = useDemoAIChat(selectedScenario, leadAccess?.sessionToken || null);

  const {
    connect,
    disconnect,
    sendTextMessage,
    isConnecting,
    isConnected,
    error: realtimeError,
  } = useDemoRealtime();

  const isLeadUnlocked = Boolean(leadAccess?.sessionToken);

  const resetDemo = useCallback(() => {
    cancelTyping();
    disconnect();
    setMode('chat');
    setInputText('');
    operatorTurnsRef.current = 0;
    isCompleteRef.current = false;
    setOperatorTurns(0);
    setIsComplete(false);
    setIsStartingVoice(false);
    setMessages([
      {
        id: createLocalId('system'),
        speaker: 'system',
        text: `Demo publica limitada a ${DEMO_LIMIT} mensagens do operador. Escolha a melhor abordagem e observe a reacao do cliente.`,
      },
      {
        id: createLocalId('client'),
        speaker: 'client',
        text: scenario.description,
      },
    ]);
  }, [cancelTyping, disconnect, scenario.description]);

  useEffect(() => {
    resetDemo();
  }, [resetDemo]);

  useEffect(() => {
    operatorTurnsRef.current = operatorTurns;
  }, [operatorTurns]);

  useEffect(() => {
    isCompleteRef.current = isComplete;
  }, [isComplete]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  useEffect(() => {
    if (aiMessages.length === 0) return;
    const last = aiMessages[aiMessages.length - 1];
    setMessages((prev) => {
      if (prev.some((item) => item.text === last.text && item.speaker === 'client')) {
        return prev;
      }
      return [...prev, { id: createLocalId('client'), speaker: 'client', text: last.text }];
    });
  }, [aiMessages]);

  const persistLeadAccess = useCallback((data: DemoLeadAccess | null) => {
    setLeadAccess(data);
    if (typeof window === 'undefined') return;
    if (data) {
      window.sessionStorage.setItem(DEMO_LEAD_STORAGE_KEY, JSON.stringify(data));
    } else {
      window.sessionStorage.removeItem(DEMO_LEAD_STORAGE_KEY);
    }
  }, []);

  const markCompletedIfNeeded = useCallback((nextTurns: number) => {
    if (nextTurns >= DEMO_LIMIT && !isCompleteRef.current) {
      cancelTyping();
      disconnect();
      isCompleteRef.current = true;
      setIsComplete(true);
      setMessages((prev) => [
        ...prev,
        {
          id: createLocalId('system'),
          speaker: 'system',
          text: 'Limite da demonstracao atingido. Crie sua conta para continuar usando chat e voz com cenarios completos.',
        },
      ]);
      return true;
    }
    return false;
  }, [cancelTyping, disconnect]);

  const appendOperatorMessage = useCallback((text: string) => {
    const trimmed = text.trim();
    if (!trimmed || isCompleteRef.current) return false;
    const nextTurns = operatorTurnsRef.current + 1;
    operatorTurnsRef.current = nextTurns;

    setMessages((prev) => [...prev, { id: createLocalId('operator'), speaker: 'operator', text: trimmed }]);
    setOperatorTurns(nextTurns);

    return !markCompletedIfNeeded(nextTurns);
  }, [markCompletedIfNeeded]);

  const handleSendChat = useCallback(() => {
    const trimmed = inputText.trim();
    if (!trimmed || isTyping || isComplete || !leadAccess?.sessionToken) return;
    const shouldContinue = appendOperatorMessage(trimmed);
    setInputText('');
    if (!shouldContinue) return;

    const nextHistory = [
      ...messages
        .filter((item) => item.speaker !== 'system')
        .map((item) => ({
          speaker: item.speaker === 'operator' ? 'operator' as const : 'client' as const,
          text: item.text,
        })),
      { speaker: 'operator' as const, text: trimmed },
    ];

    void sendMessage(trimmed, nextHistory);
  }, [appendOperatorMessage, inputText, isComplete, isTyping, leadAccess?.sessionToken, messages, sendMessage]);

  const handleConnectVoice = useCallback(async () => {
    if (isConnected || isConnecting || isComplete || !leadAccess?.sessionToken) return;
    setIsStartingVoice(true);
    try {
      await connect({
        scenarioId: selectedScenario,
        sessionToken: leadAccess.sessionToken,
        onTranscript: ({ speaker, text }) => {
          if (!text.trim()) return;
          if (speaker === 'operator') {
            const shouldContinue = appendOperatorMessage(text);
            if (!shouldContinue) return;
            return;
          }
          setMessages((prev) => [...prev, { id: createLocalId('client'), speaker: 'client', text }]);
        },
      });
    } finally {
      setIsStartingVoice(false);
    }
  }, [appendOperatorMessage, connect, isComplete, isConnected, isConnecting, leadAccess, selectedScenario]);

  const handleVoiceFallbackSend = useCallback(() => {
    const trimmed = inputText.trim();
    if (!trimmed || !isConnected || isComplete || !leadAccess?.sessionToken) return;
    const shouldContinue = appendOperatorMessage(trimmed);
    setInputText('');
    if (!shouldContinue) return;
    sendTextMessage(trimmed);
  }, [appendOperatorMessage, inputText, isComplete, isConnected, leadAccess?.sessionToken, sendTextMessage]);

  const handleLeadSubmit = useCallback(async () => {
    if (isSubmittingLead) return;
    setLeadError(null);
    setIsSubmittingLead(true);
    try {
      const data = await createDemoLead({
        ...leadForm,
        scenarioId: selectedScenario,
      });
      if (!data) {
        throw new Error('Falha ao liberar a demo.');
      }
      persistLeadAccess(data);
    } catch (error: any) {
      setLeadError(error?.message || 'Nao foi possivel liberar a demo.');
    } finally {
      setIsSubmittingLead(false);
    }
  }, [isSubmittingLead, leadForm, persistLeadAccess, selectedScenario]);

  return (
    <div style={{ backgroundColor: 'var(--background)', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <DemoHeader />

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <aside
          className="hidden md:flex"
          style={{
            width: '320px',
            borderRight: '1px solid var(--border)',
            backgroundColor: 'var(--card)',
            padding: '24px',
            flexDirection: 'column',
            gap: '24px',
            overflowY: 'auto',
          }}
        >
          <div>
            <div style={{
              fontFamily: 'var(--font-family-inter)',
              fontSize: 'var(--text-xs)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--muted-foreground)',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              marginBottom: '8px',
            }}>
              Escolha um cenario
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {SCENARIOS.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setSelectedScenario(item.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '14px 16px',
                    textAlign: 'left',
                    backgroundColor: selectedScenario === item.id ? 'color-mix(in srgb, var(--primary) 8%, transparent)' : 'var(--card)',
                    borderRadius: 'var(--radius)',
                    border: selectedScenario === item.id ? '1.5px solid var(--primary)' : '1px solid var(--border)',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: 'var(--radius-input)',
                    backgroundColor: `color-mix(in srgb, ${item.difficultyColor} 10%, transparent)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <MessageSquare size={18} style={{ color: item.difficultyColor }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontFamily: 'var(--font-family-inter)',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-weight-semibold)',
                      color: 'var(--foreground)',
                    }}>
                      {item.title}
                    </div>
                    <div style={{
                      fontFamily: 'var(--font-family-inter)',
                      fontSize: 'var(--text-xs)',
                      color: 'var(--muted-foreground)',
                    }}>
                      {item.clientPersona}
                    </div>
                  </div>
                  <ChevronRight size={16} style={{ color: 'var(--muted-foreground)' }} />
                </button>
              ))}
            </div>
          </div>

          <div style={{
            padding: '16px',
            backgroundColor: 'var(--muted)',
            borderRadius: 'var(--radius)',
            border: '1px solid var(--border)',
          }}>
            <div style={{
              fontFamily: 'var(--font-family-inter)',
              fontSize: 'var(--text-xs)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--muted-foreground)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              marginBottom: '8px',
            }}>
              Briefing
            </div>
            <p style={{
              fontFamily: 'var(--font-family-inter)',
              fontSize: 'var(--text-sm)',
              color: 'var(--foreground)',
              lineHeight: 1.7,
              margin: 0,
            }}>
              {scenario.description}
            </p>
          </div>

          <div style={{
            padding: '16px',
            backgroundColor: 'color-mix(in srgb, var(--secondary) 8%, transparent)',
            borderRadius: 'var(--radius)',
            border: '1px solid color-mix(in srgb, var(--secondary) 20%, transparent)',
          }}>
            <div style={{
              fontFamily: 'var(--font-family-inter)',
              fontSize: 'var(--text-xs)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              marginBottom: '8px',
            }}>
              Limite da demo
            </div>
            <div style={{
              fontFamily: 'var(--font-family-mono)',
              fontSize: 'var(--text-3xl)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--secondary)',
              marginBottom: '8px',
            }}>
              {operatorTurns}/{DEMO_LIMIT}
            </div>
            <p style={{
              fontFamily: 'var(--font-family-inter)',
              fontSize: 'var(--text-xs)',
              color: 'var(--muted-foreground)',
              lineHeight: 1.6,
              margin: 0,
            }}>
              Esta demonstracao publica e limitada a 10 mensagens do operador, em chat ou voz.
            </p>
          </div>

        </aside>

        <main style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{
            padding: '12px 24px',
            borderBottom: '1px solid var(--border)',
            backgroundColor: 'var(--card)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              backgroundColor: `color-mix(in srgb, ${scenario.difficultyColor} 12%, transparent)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Sparkles size={16} style={{ color: scenario.difficultyColor }} />
            </div>
            <div>
              <div style={{
                fontFamily: 'var(--font-family-inter)',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--foreground)',
              }}>
                {scenario.clientPersona} · <span style={{ color: scenario.difficultyColor }}>{scenario.clientMood}</span>
              </div>
              <div style={{
                fontFamily: 'var(--font-family-inter)',
                fontSize: 'var(--text-xs)',
                color: 'var(--muted-foreground)',
              }}>
                {scenario.title}
              </div>
            </div>
            <div style={{ flex: 1 }} />
            <div style={{ display: 'flex', gap: '8px' }}>
              {([
                { key: 'chat' as const, label: 'Mensagem', icon: MessageSquare },
                { key: 'voice' as const, label: 'Ligacao', icon: Headphones },
              ]).map((item) => (
                <button
                  key={item.key}
                  onClick={() => setMode(item.key)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 12px',
                    borderRadius: 'var(--radius-button)',
                    border: mode === item.key ? '1px solid var(--primary)' : '1px solid var(--border)',
                    backgroundColor: mode === item.key ? 'var(--primary)' : 'var(--card)',
                    color: mode === item.key ? 'var(--primary-foreground)' : 'var(--foreground)',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-family-inter)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                  }}
                >
                  <item.icon size={14} />
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {!isLeadUnlocked && (
              <div style={{
                padding: '28px',
                backgroundColor: 'var(--card)',
                borderRadius: 'var(--radius-card)',
                border: '1px solid var(--border)',
                maxWidth: '620px',
                alignSelf: 'center',
                width: '100%',
              }}>
                <h3 style={{
                  fontFamily: 'var(--font-family-inter)',
                  fontSize: 'var(--text-xl)',
                  fontWeight: 'var(--font-weight-bold)',
                  color: 'var(--foreground)',
                  marginBottom: '8px',
                }}>
                  Libere a Demo Viva
                </h3>
                <p style={{
                  fontFamily: 'var(--font-family-inter)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--muted-foreground)',
                  lineHeight: 1.7,
                  marginBottom: '20px',
                }}>
                  Para acessar a demo com chat e ligacao em tempo real, precisamos coletar um lead valido antes de liberar o uso.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px' }}>
                  {([
                    { key: 'name', label: 'Nome', placeholder: 'Seu nome completo' },
                    { key: 'email', label: 'Email', placeholder: 'voce@empresa.com' },
                    { key: 'phone', label: 'Telefone', placeholder: '(11) 99999-9999' },
                    { key: 'company', label: 'Empresa', placeholder: 'Nome da empresa' },
                    { key: 'role', label: 'Cargo', placeholder: 'Opcional' },
                  ] as const).map((field) => (
                    <label
                      key={field.key}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '6px',
                        gridColumn: field.key === 'role' ? '1 / span 2' : 'auto',
                      }}
                    >
                      <span style={{
                        fontFamily: 'var(--font-family-inter)',
                        fontSize: 'var(--text-xs)',
                        fontWeight: 'var(--font-weight-semibold)',
                        color: 'var(--foreground)',
                      }}
                      >
                        {field.label}
                      </span>
                      <input
                        value={leadForm[field.key]}
                        onChange={(e) => setLeadForm((prev) => ({ ...prev, [field.key]: e.target.value }))}
                        placeholder={field.placeholder}
                        style={{
                          padding: '12px 14px',
                          backgroundColor: 'var(--input-background)',
                          border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-input)',
                          color: 'var(--foreground)',
                          fontSize: 'var(--text-sm)',
                          fontFamily: 'var(--font-family-inter)',
                          outline: 'none',
                        }}
                      />
                    </label>
                  ))}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', marginTop: '18px', flexWrap: 'wrap' }}>
                  <div style={{
                    fontFamily: 'var(--font-family-inter)',
                    fontSize: 'var(--text-xs)',
                    color: leadError ? 'var(--error)' : 'var(--muted-foreground)',
                  }}
                  >
                    {leadError || 'A demo sera liberada apos validacao e salvamento do lead.'}
                  </div>
                  <button
                    onClick={() => void handleLeadSubmit()}
                    disabled={isSubmittingLead}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      padding: '12px 18px',
                      borderRadius: 'var(--radius-button)',
                      border: 'none',
                      backgroundColor: isSubmittingLead ? 'var(--muted)' : 'var(--primary)',
                      color: isSubmittingLead ? 'var(--muted-foreground)' : 'var(--primary-foreground)',
                      cursor: isSubmittingLead ? 'not-allowed' : 'pointer',
                      fontFamily: 'var(--font-family-inter)',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-weight-semibold)',
                    }}
                  >
                    <Sparkles size={14} />
                    {isSubmittingLead ? 'Liberando demo...' : 'Liberar Demo'}
                  </button>
                </div>
              </div>
            )}

            {isLeadUnlocked && (
              <div style={{
                padding: '12px 16px',
                backgroundColor: 'color-mix(in srgb, var(--success) 8%, transparent)',
                borderRadius: 'var(--radius)',
                border: '1px solid color-mix(in srgb, var(--success) 25%, transparent)',
                alignSelf: 'center',
                width: '100%',
                maxWidth: '620px',
              }}>
                <p style={{
                  fontFamily: 'var(--font-family-inter)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--foreground)',
                  lineHeight: 1.6,
                  margin: 0,
                }}>
                  Demo liberada para <strong>{leadAccess?.name}</strong> da <strong>{leadAccess?.company}</strong>.
                </p>
              </div>
            )}

            {isLeadUnlocked && messages.map((msg) => {
              if (msg.speaker === 'system') {
                return (
                  <div
                    key={msg.id}
                    style={{
                      padding: '12px 16px',
                      backgroundColor: 'color-mix(in srgb, var(--primary) 6%, transparent)',
                      borderRadius: 'var(--radius)',
                      border: '1px solid color-mix(in srgb, var(--primary) 15%, transparent)',
                    }}
                  >
                    <p style={{
                      fontFamily: 'var(--font-family-inter)',
                      fontSize: 'var(--text-sm)',
                      color: 'var(--foreground)',
                      lineHeight: 1.6,
                      margin: 0,
                    }}>
                      {msg.text}
                    </p>
                  </div>
                );
              }

              if (msg.speaker === 'client') {
                return (
                  <div key={msg.id} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', maxWidth: '80%' }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      flexShrink: 0,
                      backgroundColor: 'color-mix(in srgb, var(--error) 12%, transparent)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <Sparkles size={14} style={{ color: 'var(--error)' }} />
                    </div>
                    <div style={{
                      backgroundColor: 'var(--muted)',
                      borderRadius: 'var(--radius)',
                      padding: '12px 16px',
                    }}>
                      <p style={{
                        fontFamily: 'var(--font-family-inter)',
                        fontSize: 'var(--text-sm)',
                        color: 'var(--foreground)',
                        lineHeight: 1.6,
                        margin: 0,
                      }}>
                        {msg.text}
                      </p>
                    </div>
                  </div>
                );
              }

              return (
                <div
                  key={msg.id}
                  style={{
                    display: 'flex',
                    gap: '10px',
                    alignItems: 'flex-start',
                    flexDirection: 'row-reverse',
                    maxWidth: '80%',
                    alignSelf: 'flex-end',
                  }}
                >
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    flexShrink: 0,
                    backgroundColor: 'color-mix(in srgb, var(--primary) 12%, transparent)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <Headphones size={14} style={{ color: 'var(--primary)' }} />
                  </div>
                  <div style={{
                    backgroundColor: 'color-mix(in srgb, var(--primary) 8%, transparent)',
                    borderRadius: 'var(--radius)',
                    padding: '12px 16px',
                  }}>
                    <p style={{
                      fontFamily: 'var(--font-family-inter)',
                      fontSize: 'var(--text-sm)',
                      color: 'var(--foreground)',
                      lineHeight: 1.6,
                      margin: 0,
                    }}>
                      {msg.text}
                    </p>
                  </div>
                </div>
              );
            })}

            {isLeadUnlocked && isTyping && (
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: 'color-mix(in srgb, var(--error) 12%, transparent)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Sparkles size={14} style={{ color: 'var(--error)' }} />
                </div>
                <div style={{
                  backgroundColor: 'var(--muted)',
                  borderRadius: 'var(--radius)',
                  padding: '12px 16px',
                  fontFamily: 'var(--font-family-inter)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--muted-foreground)',
                }}>
                  Cliente digitando...
                </div>
              </div>
            )}

            {isLeadUnlocked && isComplete && (
              <div style={{
                padding: '28px',
                backgroundColor: 'var(--card)',
                borderRadius: 'var(--radius-card)',
                border: '1px solid var(--border)',
                textAlign: 'center',
                alignSelf: 'center',
                maxWidth: '520px',
              }}>
                <div style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '50%',
                  backgroundColor: 'color-mix(in srgb, var(--success) 12%, transparent)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                }}>
                  <Sparkles size={28} style={{ color: 'var(--success)' }} />
                </div>
                <h3 style={{
                  fontFamily: 'var(--font-family-inter)',
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-weight-bold)',
                  color: 'var(--foreground)',
                  marginBottom: '8px',
                }}>
                  Limite da Demo Atingido
                </h3>
                <p style={{
                  fontFamily: 'var(--font-family-inter)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--muted-foreground)',
                  lineHeight: 1.7,
                  marginBottom: '20px',
                }}>
                  Voce usou as {DEMO_LIMIT} mensagens disponiveis nesta demonstracao publica. Na plataforma completa, o operador continua em voz e texto com historico, avaliacao e gravacao.
                </p>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => navigate('/login')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontFamily: 'var(--font-family-inter)',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-weight-semibold)',
                      color: 'var(--primary-foreground)',
                      backgroundColor: 'var(--primary)',
                      border: 'none',
                      borderRadius: 'var(--radius-button)',
                      padding: '10px 20px',
                      cursor: 'pointer',
                    }}
                  >
                    <Sparkles size={14} /> Criar Conta Gratis
                  </button>
                </div>
              </div>
            )}

            <div ref={chatEndRef} />
          </div>

          <div style={{
            borderTop: '1px solid var(--border)',
            padding: '16px 24px',
            backgroundColor: 'var(--card)',
          }}>
            {mode === 'chat' ? (
              <div style={{ display: 'flex', gap: '10px' }}>
                <input
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSendChat(); }}
                  placeholder={isComplete ? 'Demo encerrada' : 'Digite sua resposta como operador...'}
                  disabled={isTyping || isComplete || !isLeadUnlocked}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    backgroundColor: 'var(--input-background)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-input)',
                    color: 'var(--foreground)',
                    fontSize: 'var(--text-sm)',
                    fontFamily: 'var(--font-family-inter)',
                    outline: 'none',
                  }}
                />
                <button
                  onClick={handleSendChat}
                  disabled={!inputText.trim() || isTyping || isComplete || !isLeadUnlocked}
                  style={{
                    padding: '0 16px',
                    backgroundColor: inputText.trim() && !isTyping && !isComplete && isLeadUnlocked ? 'var(--primary)' : 'var(--muted)',
                    color: inputText.trim() && !isTyping && !isComplete && isLeadUnlocked ? 'var(--primary-foreground)' : 'var(--muted-foreground)',
                    borderRadius: 'var(--radius-button)',
                    border: 'none',
                    cursor: inputText.trim() && !isTyping && !isComplete && isLeadUnlocked ? 'pointer' : 'not-allowed',
                  }}
                >
                  <Send size={16} />
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                {isConnected ? (
                  <div
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '10px',
                      padding: '12px 16px',
                      borderRadius: 'var(--radius-button)',
                      backgroundColor: 'color-mix(in srgb, var(--success) 12%, transparent)',
                      color: 'var(--success)',
                      border: '1px solid color-mix(in srgb, var(--success) 35%, var(--border))',
                      fontFamily: 'var(--font-family-inter)',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-weight-semibold)',
                    }}
                  >
                    <Mic size={18} />
                    Ligacao ativa
                  </div>
                ) : (
                  <button
                    onClick={() => void handleConnectVoice()}
                    disabled={isConnecting || isStartingVoice || isComplete || !isLeadUnlocked}
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '10px',
                      padding: '12px 16px',
                      borderRadius: 'var(--radius-button)',
                      backgroundColor: isConnecting || isStartingVoice || isComplete || !isLeadUnlocked ? 'var(--muted)' : 'var(--primary)',
                      color: isConnecting || isStartingVoice || isComplete || !isLeadUnlocked ? 'var(--muted-foreground)' : 'var(--primary-foreground)',
                      border: 'none',
                      cursor: isConnecting || isStartingVoice || isComplete || !isLeadUnlocked ? 'not-allowed' : 'pointer',
                      fontFamily: 'var(--font-family-inter)',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-weight-semibold)',
                    }}
                  >
                    <Mic size={18} />
                    {isConnecting || isStartingVoice ? 'Conectando ligacao...' : 'Conectar ligacao com IA'}
                  </button>
                )}

                <div style={{ display: 'flex', gap: '8px', minWidth: '260px' }}>
                  <input
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleVoiceFallbackSend(); }}
                    placeholder="Ou digite durante a ligacao..."
                  disabled={!isConnected || isComplete || !isLeadUnlocked}
                    style={{
                      flex: 1,
                      padding: '12px 16px',
                      backgroundColor: 'var(--input-background)',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-input)',
                      color: 'var(--foreground)',
                      fontSize: 'var(--text-sm)',
                      fontFamily: 'var(--font-family-inter)',
                      outline: 'none',
                    }}
                  />
                  <button
                    onClick={handleVoiceFallbackSend}
                    disabled={!inputText.trim() || !isConnected || isComplete || !isLeadUnlocked}
                    style={{
                      padding: '0 16px',
                      backgroundColor: inputText.trim() && isConnected && !isComplete && isLeadUnlocked ? 'var(--primary)' : 'var(--muted)',
                      color: inputText.trim() && isConnected && !isComplete && isLeadUnlocked ? 'var(--primary-foreground)' : 'var(--muted-foreground)',
                      borderRadius: 'var(--radius-button)',
                      border: 'none',
                      cursor: inputText.trim() && isConnected && !isComplete && isLeadUnlocked ? 'pointer' : 'not-allowed',
                    }}
                  >
                    <Send size={16} />
                  </button>
                </div>
              </div>
            )}

            <div style={{
              marginTop: '8px',
              fontFamily: 'var(--font-family-inter)',
              fontSize: 'var(--text-xs)',
              color: 'var(--muted-foreground)',
            }}>
              {mode === 'voice'
                ? isConnected
                  ? `Fale normalmente. Cada fala do operador conta como 1 mensagem enviada no limite de ${DEMO_LIMIT}.`
                  : isLeadUnlocked ? 'Conecte a ligacao para testar a voz em tempo real.' : 'Preencha seus dados para liberar a ligacao em tempo real.'
                : isLeadUnlocked ? `Cada mensagem enviada conta para o limite de ${DEMO_LIMIT} mensagens.` : 'Preencha seus dados para liberar o chat da demo.'}
            </div>
            {(aiError || realtimeError) && (
              <div style={{
                marginTop: '8px',
                fontFamily: 'var(--font-family-inter)',
                fontSize: 'var(--text-xs)',
                color: 'var(--error)',
              }}>
                {aiError || realtimeError}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

export default InteractiveDemo;
